import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpService } from '../../http.service';

// Panel / back-office Line Win/Loss tree (client 2026-06-17: "tenant 要看线的输赢，谁输谁赢 3 level,
// clickable to narrow into line or back" → "i want in panel"). With no agent selected the tree is
// rooted at a synthetic company root showing every top-level agent line; clicking an agent narrows
// into that line, and the breadcrumb walks back up. Win/loss is shown from the company side
// (positive = the line LOST money to the house = green; negative = players won = red).
@Component({
  selector: 'app-line-winloss',
  templateUrl: './line-winloss.component.html',
  styleUrls: ['./line-winloss.component.scss'],
})
export class LineWinlossComponent implements OnInit {
  public company_uuid = '';
  public is_loading = false;

  public agent_code = '';                 // current root (empty = company-wide)
  public company_wide = true;
  public root: any = { code: '', name: '' };
  public summary: any = null;
  public tree: any[] = [];

  public search_agent = '';               // agent-code filter box (jump straight to a line)
  public error_msg = '';

  // Breadcrumb trail of agents drilled through (excludes the current root).
  public trail: { code: string; name: string }[] = [];

  public from_date = '';
  public to_date = '';

  constructor(
    private http: HttpService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    this.from_date = this.fmt(firstDayOfMonth);
    this.to_date = this.fmt(today);

    // Trail + dates are carried through navigation state so narrow-in / back can rebuild the breadcrumb.
    const nav = this.router.getCurrentNavigation();
    const st = nav?.extras?.state as any;
    if (st && Array.isArray(st.trail)) this.trail = st.trail;
    if (st && st.from_date) this.from_date = st.from_date;
    if (st && st.to_date) this.to_date = st.to_date;
  }

  private fmt(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  ngOnInit() {
    this.company_uuid = this.http.getStaticSelectedCompany();
    this.route.params.subscribe((params) => {
      this.agent_code = params['agent_code'] || '';
      this.search_agent = this.agent_code;
      this.load();
    });
  }

  async load() {
    this.is_loading = true;
    this.error_msg = '';
    try {
      const body: any = { from_date: this.from_date, to_date: this.to_date };
      if (this.agent_code) body.agent_code = this.agent_code;
      const t: any = await this.http.post('/admin/agent/line-winloss', body);
      if (t && t.status) {
        this.company_wide = !!t.company_wide;
        this.root = t.root || { code: this.agent_code, name: '' };
        this.summary = t.summary || null;
        this.tree = t.tree || [];
      } else {
        this.tree = [];
        this.summary = null;
        this.error_msg = (t && t.message) || 'No data found';
      }
    } finally {
      this.is_loading = false;
    }
  }

  filter() {
    this.load();
  }

  // Agent-code filter: jump straight to a specific line (fresh breadcrumb rooted at Company),
  // or clear back to the company-wide view when the box is empty.
  searchByAgent() {
    const code = (this.search_agent || '').trim();
    if (!code) {
      this.jumpTo(-1);
      return;
    }
    this.router.navigate(['/lo', this.company_uuid, 'agent-line-winloss', code], {
      state: { trail: [{ code: '', name: 'Company' }], from_date: this.from_date, to_date: this.to_date },
    });
  }

  clearAgentSearch() {
    this.search_agent = '';
    this.jumpTo(-1);
  }

  // Narrow into a sub-agent line: push the current root onto the trail and re-root.
  drillInto(node: any) {
    if (!node || !node.can_drill) return;
    const crumb = this.company_wide
      ? { code: '', name: 'Company' }
      : { code: this.root.code, name: this.root.name };
    const newTrail = [...this.trail, crumb];
    this.router.navigate(['/lo', this.company_uuid, 'agent-line-winloss', node.code], {
      state: { trail: newTrail, from_date: this.from_date, to_date: this.to_date },
    });
  }

  // Jump to a breadcrumb ancestor (index in trail). index = -1 means company-wide root.
  jumpTo(index: number) {
    if (index < 0) {
      this.router.navigate(['/lo', this.company_uuid, 'agent-line-winloss'], {
        state: { trail: [], from_date: this.from_date, to_date: this.to_date },
      });
      return;
    }
    const target = this.trail[index];
    const newTrail = this.trail.slice(0, index);
    if (!target.code) {
      // ancestor crumb is the company root
      this.router.navigate(['/lo', this.company_uuid, 'agent-line-winloss'], {
        state: { trail: newTrail, from_date: this.from_date, to_date: this.to_date },
      });
      return;
    }
    this.router.navigate(['/lo', this.company_uuid, 'agent-line-winloss', target.code], {
      state: { trail: newTrail, from_date: this.from_date, to_date: this.to_date },
    });
  }

  goUp() {
    if (this.trail.length === 0) {
      if (this.agent_code) this.jumpTo(-1);
      return;
    }
    this.jumpTo(this.trail.length - 1);
  }

  // Player-perspective status -> bootstrap colour token.
  statusClass(status: string): string {
    if (status === 'winning') return 'text-danger'; // players ahead = risk to house
    if (status === 'losing') return 'text-success';  // players behind = good for house
    return 'text-muted';
  }

  statusBadgeClass(status: string): string {
    if (status === 'winning') return 'bg-danger';
    if (status === 'losing') return 'bg-success';
    return 'bg-secondary';
  }

  statusLabel(status: string): string {
    if (status === 'winning') return 'Players Winning';
    if (status === 'losing') return 'Players Losing';
    return 'Even';
  }
}
